// src/pages/LandingPage.tsx
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * OnlyKickers landing — "The Special Teams Record Book".
 * Deadpan reverence: the most overlooked players in football, presented with the
 * gravity of an official league record. Display = Anton (scoreboard), body =
 * Source Serif 4 (almanac), accent = "upright gold". Scoped under .lp-root.
 */

const RULES = [
  {
    tag: 'Roster',
    title: 'Six men. No skill positions.',
    desc: 'Three kickers and three punters. Start two of each, every week. Nobody else gets in.',
  },
  {
    tag: 'Scoring',
    title: 'Real NFL scoring.',
    desc: 'Field goals, PATs, distance bonuses, net punt average. Every leg counts, exactly as it happens on Sunday.',
  },
  {
    tag: 'Matchups',
    title: 'Head-to-head, all season.',
    desc: 'A different league rival every week. Your kickers against theirs. Someone walks away 1-0.',
  },
  {
    tag: 'Playoffs',
    title: 'Top four. Win or go home.',
    desc: 'The bracket only cares about your record. Split the uprights when it matters or watch from the couch.',
  },
];

const DICKER = [
  { v: '171.0', l: 'Fantasy Points' },
  { v: '92.7%', l: 'FG Accuracy' },
  { v: '59', l: 'Longest FG (yds)' },
  { v: '38/41', l: 'Field Goals Made' },
];

const FEATURE_SHOT = {
  src: '/landing/draft-room.png',
  alt: 'OnlyKickers draft room: a board of available kickers and punters with fantasy points, a live "on the clock" panel, and the snake-draft order down the right side',
  caption: 'Snake-draft your kickers and punters. Yes, you will agonize over the third punter.',
  pos: 'left top',
};

const SHOTS = [
  {
    src: '/landing/season-standings.png',
    alt: 'Season standings table with win-loss records and a red line marking the playoff cutoff',
    caption: 'Real standings. Real playoff cutline.',
    pos: 'center top',
  },
  {
    src: '/landing/weekly-matchups.png',
    alt: 'Week 17 head-to-head matchups, each showing two teams and their combined kicker and punter scores',
    caption: 'Head-to-head every week. Real scores, real trash talk.',
    pos: 'center center',
  },
];

/** Field-goal uprights, drawn in gold. The kicker's entire world. */
function Goalpost({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 240 320"
      className={className}
      fill="none"
      stroke="#F4B400"
      strokeWidth="10"
      strokeLinecap="square"
      aria-hidden="true"
    >
      {/* base post rising out of the ground */}
      <line x1="120" y1="320" x2="120" y2="150" />
      {/* crossbar */}
      <line x1="40" y1="150" x2="200" y2="150" />
      {/* uprights */}
      <line x1="40" y1="150" x2="40" y2="20" />
      <line x1="200" y1="150" x2="200" y2="20" />
    </svg>
  );
}

export default function LandingPage() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="lp-root min-h-screen bg-paper text-ink-900 antialiased">
      {/* ===== Masthead ===== */}
      <header className="border-b-2 border-ink-900">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-baseline gap-2" aria-label="OnlyKickers home">
              <span
                className="font-display text-2xl sm:text-3xl tracking-tight leading-none text-ink-900"
                style={{ letterSpacing: '-0.01em' }}
              >
                ONLY<span className="text-gold-700">KICKERS</span>
              </span>
            </Link>
            <nav className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/login"
                className="px-3 py-2 text-sm font-semibold uppercase tracking-wide text-ink-700 hover:text-ink-900 whitespace-nowrap"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 text-sm font-bold uppercase tracking-wide bg-ink-900 text-paper hover:bg-ink-800 transition-colors whitespace-nowrap"
              >
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* running masthead strip */}
      <div className="bg-ink-900 text-paper-300 on-ink">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-1.5 flex items-center justify-center gap-3 text-[0.7rem] sm:text-xs uppercase tracking-[0.25em] font-semibold">
          <span>Season-Long</span>
          <span className="text-gold">•</span>
          <span>Kickers &amp; Punters Only</span>
          <span className="text-gold">•</span>
          <span>Est. 2026</span>
        </div>
      </div>

      <main>
        {/* ===== Hero ===== */}
        <section className="relative overflow-hidden border-b-2 border-ink-900">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="animate-rise font-semibold uppercase tracking-[0.22em] text-xs sm:text-sm text-ink-600 mb-5">
                The official league of special teams
              </p>
              <h1
                className="animate-rise font-display uppercase text-ink-900 leading-[0.92] mb-6"
                style={{
                  fontSize: 'clamp(2.75rem, 8vw, 5.5rem)',
                  letterSpacing: '-0.02em',
                  textWrap: 'balance',
                  animationDelay: '80ms',
                }}
              >
                Finally, a league where the kicker goes{' '}
                <span className="relative whitespace-nowrap">
                  first
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-1 h-[0.14em] bg-gold"
                  />
                </span>
                .
              </h1>
              <p
                className="animate-rise text-lg sm:text-xl text-ink-700 max-w-2xl leading-relaxed mb-8"
                style={{ animationDelay: '160ms', textWrap: 'pretty' }}
              >
                Season-long fantasy football for kickers and punters — the most
                disrespected players in the sport, drafted at last like the
                franchise cornerstones they were always meant to be. Real NFL
                scoring. Real head-to-head matchups. A real playoff race.
              </p>
              <div
                className="animate-rise flex flex-col sm:flex-row gap-3 sm:items-center"
                style={{ animationDelay: '240ms' }}
              >
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-gold text-ink-900 font-display text-lg uppercase tracking-wide border-2 border-ink-900 shadow-[4px_4px_0_0_#1B1A17] hover:shadow-[2px_2px_0_0_#1B1A17] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150"
                >
                  Create Your League
                </Link>
                <a
                  href="#rulebook"
                  className="inline-flex items-center justify-center px-5 py-3.5 font-semibold uppercase tracking-wide text-sm text-ink-800 hover:text-ink-900 underline decoration-gold decoration-2 underline-offset-4"
                >
                  Read the rulebook
                </a>
              </div>
            </div>

            {/* Goalpost anchor — the deadpan gag */}
            <div className="lg:col-span-4 hidden lg:flex flex-col items-center justify-center">
              <Goalpost className="w-44 h-56" />
              <div className="mt-4 text-center">
                <span className="block font-display text-gold-700 text-sm uppercase tracking-widest">
                  ↑ Your first-round pick
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ===== The Rulebook ===== */}
        <section id="rulebook" className="border-b-2 border-ink-900 bg-paper-100">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
            <div className="flex items-end justify-between border-b-2 border-ink-900 pb-3 mb-8 sm:mb-10">
              <h2 className="font-display uppercase text-ink-900 text-3xl sm:text-4xl leading-none">
                The Rulebook
              </h2>
              <span className="font-semibold uppercase tracking-widest text-xs text-ink-600 hidden sm:block">
                Four articles
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-px bg-ink-300">
              {RULES.map((r, i) => (
                <article key={r.tag} className="bg-paper-100 p-6 sm:p-8">
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-display text-gold-700 text-2xl leading-none">
                      0{i + 1}
                    </span>
                    <span className="font-semibold uppercase tracking-[0.2em] text-xs text-ink-600 pt-1">
                      {r.tag}
                    </span>
                  </div>
                  <h3 className="font-display uppercase text-ink-900 text-xl sm:text-2xl leading-tight mb-2">
                    {r.title}
                  </h3>
                  <p className="text-ink-700 leading-relaxed">{r.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===== For the Record (Dicker exhibit) ===== */}
        <section className="bg-ink-900 text-paper on-ink border-b-2 border-ink-900">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
              <h2 className="font-display uppercase text-paper text-3xl sm:text-4xl leading-none">
                For the Record
              </h2>
              <span className="font-semibold uppercase tracking-[0.2em] text-xs text-gold">
                Exhibit A
              </span>
            </div>
            <p className="text-paper-300 mb-10 max-w-2xl text-lg leading-relaxed">
              Cameron Dicker · Los Angeles Chargers · Kicker · 2025 regular season.
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink-700 border border-ink-700 mb-8">
              {DICKER.map((s) => (
                <div key={s.l} className="bg-ink-900 px-5 py-7 sm:py-9 text-center">
                  <div className="font-display text-gold text-4xl sm:text-5xl lg:text-6xl leading-none">
                    {s.v}
                  </div>
                  <div className="mt-3 text-xs sm:text-sm uppercase tracking-wide text-paper-300">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>

            <p className="font-serif text-xl sm:text-2xl text-paper leading-snug max-w-3xl">
              He outscored half the running backs in your old league.
              <span className="text-gold"> He went undrafted.</span>
            </p>
          </div>
        </section>

        {/* ===== The league, in session (screenshots) ===== */}
        <section className="border-b-2 border-ink-900">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
            <div className="flex items-end justify-between border-b-2 border-ink-900 pb-3 mb-8 sm:mb-10">
              <h2 className="font-display uppercase text-ink-900 text-3xl sm:text-4xl leading-none">
                The League, In Session
              </h2>
              <span className="font-semibold uppercase tracking-widest text-xs text-ink-600 hidden sm:block">
                Live product
              </span>
            </div>
            {/* Featured shot — large and legible */}
            <figure className="border-2 border-ink-900 bg-paper-100 mb-6 lg:mb-8">
              <div className="flex items-center gap-1.5 px-3 h-8 border-b-2 border-ink-900 bg-paper-200">
                <span className="w-2.5 h-2.5 rounded-full bg-ink-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-ink-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-ink-300" />
                <span className="ml-2 text-xs text-ink-500 truncate">onlykickers.com/app · draft</span>
              </div>
              <div
                className="overflow-hidden bg-white"
                style={{ height: 'clamp(240px, 38vw, 460px)' }}
              >
                <img
                  src={FEATURE_SHOT.src}
                  alt={FEATURE_SHOT.alt}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: FEATURE_SHOT.pos }}
                />
              </div>
              <figcaption className="px-5 py-4 border-t-2 border-ink-900 text-ink-700 leading-snug text-lg">
                {FEATURE_SHOT.caption}
              </figcaption>
            </figure>

            <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
              {SHOTS.map((s) => (
                <figure key={s.src} className="border-2 border-ink-900 bg-paper-100">
                  <div className="flex items-center gap-1.5 px-3 h-8 border-b-2 border-ink-900 bg-paper-200">
                    <span className="w-2.5 h-2.5 rounded-full bg-ink-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-ink-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-ink-300" />
                  </div>
                  <div className="overflow-hidden bg-white" style={{ height: '300px' }}>
                    <img
                      src={s.src}
                      alt={s.alt}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: s.pos }}
                    />
                  </div>
                  <figcaption className="px-4 py-4 border-t-2 border-ink-900 text-ink-700 leading-snug">
                    {s.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Closing CTA ===== */}
        <section className="bg-gold border-b-2 border-ink-900">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
            <h2
              className="font-display uppercase text-ink-900 leading-[0.95] mb-6"
              style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)', letterSpacing: '-0.02em', textWrap: 'balance' }}
            >
              Draft the guys
              <br className="hidden sm:block" /> everyone else forgets.
            </h2>
            <p className="text-ink-800 text-lg sm:text-xl max-w-xl mx-auto mb-8 leading-relaxed">
              Start a league, invite your group chat, and find out who actually
              understands special teams.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-ink-900 text-paper font-display text-lg sm:text-xl uppercase tracking-wide border-2 border-ink-900 hover:bg-ink-800 transition-colors"
            >
              Create Your League
            </Link>
          </div>
        </section>
      </main>

      {/* ===== Colophon / footer ===== */}
      <footer className="bg-ink-900 text-paper-300 on-ink">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <span className="font-display text-2xl text-paper leading-none">
                ONLY<span className="text-gold">KICKERS</span>
              </span>
              <p className="mt-3 max-w-sm text-paper-300 leading-relaxed">
                The only fantasy platform where Cameron Dicker goes in the first
                round.
              </p>
            </div>
            <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <Link to="/privacy" className="text-paper-300 hover:text-gold">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-paper-300 hover:text-gold">
                Terms of Service
              </Link>
              <a
                href="mailto:support@onlykickers.com"
                className="text-paper-300 hover:text-gold"
              >
                support@onlykickers.com
              </a>
            </nav>
          </div>
          <div className="mt-8 pt-5 border-t border-ink-700 text-xs uppercase tracking-widest text-paper-400">
            © 2026 OnlyKickers
          </div>
        </div>
      </footer>
    </div>
  );
}
